import { useEffect, useRef } from 'react';
import { useStore } from '../app/store';
import { drawZone, ui } from '../app/ui';
import * as act from '../app/actions';
import { openDialog } from '../app/dialogs';
import { plural } from '../app/words';
import type { Model, Person, ZoneShown } from '../data/model';
import { spotOf } from '../data/waiting';
import { areaKm2, inside, MAX_NAME } from '../data/zones';
import { finishDrawing, ZoneMap, zoneEdit } from '../map/ZoneMap';
import { Av, EmptyHelp, More } from '../ui/bits';
import { Icon } from '../ui/Icon';

/**
 * Couriers → Zones: the zones the owner drew, beside Google's map. Each has a name, the couriers who cover it and its
 * shape; when stops are shared out a stop inside it goes only to them (src/data/share.ts). Right-click a zone, here or on
 * the map, and ⌘K, carry the same actions (menus.ts `zoneMenu`).
 */

/** "Musa and Tunde", or what having none means. */
export function zoneWho(m: Model, z: ZoneShown): string {
  const names = z.couriers.map((u) => m.person(u)?.first).filter((x): x is string => !!x);
  if (!names.length) return 'No couriers yet: it doesn’t limit who gets its stops';
  return names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
}

/** How many stops waiting for a route are inside it. */
export const waitingIn = (m: Model, z: ZoneShown) => m.waiting.filter((w) => {
  const at = spotOf(w);
  return !!at && inside(at, z.path);
}).length;

export function ZonesView({ m }: { m: Model }) {
  const s = useStore(ui);
  const finish = () => finishDrawing(m, act.saveNewZone);
  // Enter closes the zone being drawn; ⌫ takes its last corner off (not while typing in its name).
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      const d = ui.get().drawing;
      if (!d) return;
      const typing = (document.activeElement as HTMLElement | null)?.tagName === 'INPUT';
      if (e.key === 'Enter' && d.path.length >= 3) {
        e.preventDefault();
        finish();
      } else if ((e.key === 'Backspace' || e.key === 'Delete') && !typing && d.path.length) {
        e.preventDefault();
        ui.set({ drawing: { ...d, path: d.path.slice(0, -1) } });
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [m]);
  return (
    <div className="zonesview card">
      <aside className="list" aria-label="Zones">
        {s.drawing ? <Drawing m={m} finish={finish} /> : <ZoneList m={m} />}
      </aside>
      <ZoneMap m={m} onFinish={finish} />
    </div>
  );
}

function ZoneList({ m }: { m: Model }) {
  const s = useStore(ui);
  return (
    <>
      <div className="lhead">
        <div className="row">
          <span className="title grow">Zones</span>
          <button className="btn sm strong" onClick={() => drawZone()}>
            <Icon name="polygon" size="sm" />
            Draw a zone
          </button>
        </div>
        <div className="cap" style={{ marginTop: 6 }}>
          When stops are shared out, a stop inside a zone goes only to its couriers; where zones overlap, to any of theirs. A
          stop outside every zone can go to anyone.
        </div>
      </div>
      {m.data.zonesUnreadable && <div className="empty cap">Zones can’t be read yet: Routr’s server needs an update first.</div>}
      {m.zones.length ? (
        <div className="rows">
          {m.zones.map((z) => (
            <ZoneRow key={z.id} m={m} z={z} open={s.zone === z.id} />
          ))}
        </div>
      ) : (
        !m.data.zonesUnreadable && (
          <EmptyHelp
            title="No zones yet."
            tries={[
              { icon: 'polygon', label: 'Draw a zone', note: 'Click round a district on the map; a few corners do', act: 'draw' },
              ...(m.people.length ? [] : [{ icon: 'userPlus', label: 'Invite a courier', note: 'A zone is for the couriers who cover it', act: 'invite' }]),
            ]}
            onTry={(a) => (a === 'draw' ? drawZone() : openDialog({ kind: 'invite' }))}
          />
        )
      )}
    </>
  );
}

function ZoneRow({ m, z, open }: { m: Model; z: ZoneShown; open: boolean }) {
  const s = useStore(ui);
  const n = waitingIn(m, z);
  return (
    <div className={`zrow${open ? ' on' : ''}`} data-ctx={`zone:${z.id}`} style={{ '--c': z.hex } as React.CSSProperties}>
      <div className="item" role="button" tabIndex={0} aria-expanded={open} onClick={() => ui.set({ zone: open ? null : z.id, reshaping: false, renaming: false })}>
        <span className="zsw" aria-hidden="true" />
        <div className="grow" style={{ minWidth: 0 }}>
          <b className="ellipsis">{z.name}</b>
          <div className="cap">{zoneWho(m, z)}</div>
          <div className="cap">
            About {areaKm2(z.path) < 10 ? areaKm2(z.path).toFixed(1) : Math.round(areaKm2(z.path))} km² · {n ? `${plural(n, 'stop')} waiting inside` : 'no stops waiting inside'}
          </div>
        </div>
        <More menu={`zone:${z.id}`} />
      </div>
      {open && (s.reshaping ? <Reshaping m={m} z={z} /> : <ZoneRecord m={m} z={z} />)}
    </div>
  );
}

function ZoneRecord({ m, z }: { m: Model; z: ZoneShown }) {
  const s = useStore(ui);
  const name = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (s.renaming) {
      name.current?.focus();
      name.current?.select();
    }
  }, [s.renaming]);
  const save = () => {
    act.renameZone(z.id, name.current?.value ?? '');
    ui.set({ renaming: false });
  };
  return (
    <div className="zrec">
      <label className="field">
        <Icon name="notePencil" />
        <input
          ref={name}
          key={z.id + z.name}
          defaultValue={z.name}
          maxLength={MAX_NAME}
          aria-label="The zone’s name"
          autoComplete="off"
          spellCheck={false}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') {
              (e.target as HTMLInputElement).value = z.name;
              (e.target as HTMLInputElement).blur();
            }
          }}
        />
      </label>
      <div className="eyebrow" style={{ margin: '14px 0 6px' }}>
        Who covers it
      </div>
      <Couriers m={m} picked={z.couriers} onToggle={(uid) => act.toggleZoneCourier(z.id, uid)} />
      <div className="acts" style={{ marginTop: 12 }}>
        <button className="mini" onClick={() => ui.set({ reshaping: true })}>
          <Icon name="polygon" size="sm" />
          Change the shape
        </button>
        <button className="mini" onClick={() => act.deleteZone(z.id)}>
          <Icon name="trash" size="sm" />
          Delete
        </button>
      </div>
    </div>
  );
}

/** The couriers to tick: the first ticked gives the zone its colour. */
function Couriers({ m, picked, onToggle }: { m: Model; picked: string[]; onToggle: (uid: string) => void }) {
  if (!m.people.length) return <div className="cap">No couriers in your business yet: invite one, then tick them here.</div>;
  return (
    <div className="zpeople">
      {m.people.map((p: Person) => {
        const on = picked.includes(p.uid);
        return (
          <button key={p.uid} className={`zp${on ? ' on' : ''}`} role="checkbox" aria-checked={on} onClick={() => onToggle(p.uid)}>
            <Av p={p} size="sm" />
            <span className="grow ellipsis">{p.first}</span>
            <span className={`cbx${on ? ' on' : ''}`} aria-hidden="true">
              {on && <Icon name="check" />}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function Drawing({ m, finish }: { m: Model; finish: () => void }) {
  const s = useStore(ui);
  const d = s.drawing!;
  const n = d.path.length;
  return (
    <>
      <div className="lhead">
        <div className="row">
          <button className="iconbtn sm" data-tip="Stop drawing · Esc" aria-label="Stop drawing" onClick={() => ui.set({ drawing: null })}>
            <Icon name="arrowLeft" size="lg" />
          </button>
          <span className="title grow">New zone</span>
        </div>
        <label className="field" style={{ marginTop: 12 }}>
          <Icon name="notePencil" />
          <input
            placeholder={`Its name: Wuse 2, the north side… (or Zone ${m.zones.length + 1})`}
            value={d.name}
            maxLength={MAX_NAME}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            aria-label="The zone’s name"
            onChange={(e) => ui.set({ drawing: { ...d, name: e.target.value } })}
            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
          />
        </label>
        <div className="eyebrow" style={{ margin: '14px 0 6px' }}>
          Who covers it
        </div>
        <Couriers m={m} picked={d.couriers} onToggle={(uid) => ui.set({ drawing: { ...d, couriers: d.couriers.includes(uid) ? d.couriers.filter((x) => x !== uid) : [...d.couriers, uid] } })} />
        <div className="zhint">
          <Icon name="crosshair" />
          <div>
            <b>{n ? `${plural(n, 'corner')} so far` : 'Click the map to add corners'}</b>
            <div className="cap">
              {n < 3 ? `At least 3. ` : 'Click the first corner, or Done, to close it. '}
              {n ? '⌫ takes the last one off.' : 'A district’s main roads make good edges.'}
            </div>
          </div>
        </div>
        <div className="row" style={{ marginTop: 12, gap: 8 }}>
          <button className="btn primary grow" disabled={n < 3} onClick={finish}>
            <Icon name="check" />
            {n < 3 ? `Done (${3 - n} more corner${3 - n === 1 ? '' : 's'})` : 'Done'}
          </button>
          <button className="btn" onClick={() => ui.set({ drawing: null })}>
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}

function Reshaping({ z }: { m: Model; z: ZoneShown }) {
  const edit = useStore(zoneEdit);
  return (
    <div className="zrec">
      <div className="zhint" style={{ marginTop: 0 }}>
        <Icon name="polygon" />
        <div>
          <b>Changing {z.name}’s shape</b>
          <div className="cap">Drag a corner, or a midpoint to add one. Right-click a corner to take it off.</div>
        </div>
      </div>
      <div className="row" style={{ marginTop: 12, gap: 8 }}>
        <button className="btn primary sm" onClick={() => act.reshapeZone(z.id, edit.path ?? z.path)}>
          Save the shape
        </button>
        <button className="btn sm" onClick={() => ui.set({ reshaping: false })}>
          Cancel
        </button>
      </div>
    </div>
  );
}
